import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaCalendarAlt, FaMapMarkerAlt, FaUniversity, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import PageTemplate from '../../components/Common/PageTemplate';

const DavutAksutUSMZiyareti = () => {
  // Fotoğraf URL'leri
  const images = [
    'https://fs.hacettepe.edu.tr/fenyeni/fotogaleri/1.jfif',
    'https://fs.hacettepe.edu.tr/fenyeni/fotogaleri/3.jfif'
  ];

  const [modalOpen, setModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState({});

  // Modal açma fonksiyonu
  const openModal = useCallback((index) => {
    setCurrentImageIndex(index);
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  // Modal kapatma fonksiyonu
  const closeModal = useCallback(() => {
    setModalOpen(false);
    document.body.style.overflow = 'unset';
  }, []);

  // Sonraki fotoğraf
  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  // Önceki fotoğraf
  const prevImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  // Klavye kontrolü
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!modalOpen) return;

      switch(e.key) {
        case 'Escape':
          closeModal();
          break;
        case 'ArrowRight':
          nextImage();
          break;
        case 'ArrowLeft':
          prevImage();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [modalOpen, closeModal, nextImage, prevImage]);

  // Fotoğraf yüklenme durumu
  const handleImageLoad = useCallback((index) => {
    setImageLoaded(prev => ({ ...prev, [index]: true }));
  }, []);

  // Preload next and previous images
  useEffect(() => {
    if (modalOpen) {
      const preloadIndexes = [
        (currentImageIndex + 1) % images.length,
        (currentImageIndex - 1 + images.length) % images.length
      ];

      preloadIndexes.forEach(index => {
        const img = new Image();
        img.src = images[index];
      });
    }
  }, [currentImageIndex, modalOpen, images]);

  // Modal component
  const ImageModal = () => {
    if (!modalOpen) return null;

    return (
      <div
        className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center"
        onClick={closeModal}
      >
        {/* Close button */}
        <button
          onClick={closeModal}
          className="absolute top-4 right-4 z-60 text-white hover:text-gray-300 transition-colors"
        >
          <FaTimes size={24} />
        </button>

        {/* Image counter */}
        <div className="absolute top-4 left-4 z-60 text-white bg-black bg-opacity-50 px-3 py-1 rounded-full text-sm">
          {currentImageIndex + 1} / {images.length}
        </div>

        {/* Previous button */}
        {images.length > 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-4 z-60 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-2"
          >
            <FaChevronLeft size={20} />
          </button>
        )}

        {/* Next button */}
        {images.length > 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-4 z-60 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-2"
          >
            <FaChevronRight size={20} />
          </button>
        )}

        {/* Main image */}
        <div
          className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={images[currentImageIndex]}
            alt={`USM Ziyareti ${currentImageIndex + 1}`}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            loading="eager"
          />
        </div>

        {/* Image title */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-60 text-white bg-black bg-opacity-50 px-4 py-2 rounded-full text-sm">
          USM Ziyareti - Fotoğraf {currentImageIndex + 1}
        </div>
      </div>
    );
  };

  return (
    <PageTemplate
      title="Öğr. Gör. Dr. Davut Aksüt'den Universiti Sains Malaysia'ya (USM) ye işbirliğimiz kapsamında Akademik Ziyaret"
      subtitle="Hacettepe Üniversitesi ile Universiti Sains Malaysia arasındaki akademik iş birliğini güçlendirecek stratejik temaslarda bulunuldu."
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Ana İçerik */}
        <div className="bg-white p-8 md:p-12 rounded-3xl shadow-xl -mt-16 relative z-10 mb-16">
          {/* Ziyaret Bilgileri */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="flex items-center justify-center md:justify-start space-x-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
              <FaCalendarAlt className="text-blue-600 text-xl flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-600 uppercase tracking-wide">Tarih</p>
                <p className="text-gray-800 font-semibold">23 Aralık 2025</p>
              </div>
            </div>

            <div className="flex items-center justify-center md:justify-start space-x-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-100">
              <FaMapMarkerAlt className="text-green-600 text-xl flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-600 uppercase tracking-wide">Yer</p>
                <p className="text-gray-800 font-semibold">Penang, Malezya</p>
                <p className="text-gray-600 text-sm">USM Mühendislik Kampüsü</p>
              </div>
            </div>

            <div className="flex items-center justify-center md:justify-start space-x-3 p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-2xl border border-purple-100">
              <FaUniversity className="text-purple-600 text-xl flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-purple-600 uppercase tracking-wide">Kurum</p>
                <p className="text-gray-800 font-semibold">Universiti Sains Malaysia</p>
                <p className="text-gray-600 text-sm">Resmi Ziyaret</p>
              </div>
            </div>
          </div>

          {/* Ana Metin */}
          <div className="prose prose-lg max-w-none space-y-6">
            <div className="bg-gradient-to-r from-hacettepe-blue/5 to-hacettepe-red/5 p-8 rounded-2xl border-l-4 border-hacettepe-blue">
              <p className="text-xl leading-relaxed text-gray-700 mb-6">
                Fakültemiz öğretim üyelerinden <span className="font-semibold text-hacettepe-blue">Öğr. Gör. Dr. Davut Aksüt</span>,
                <span className="font-semibold"> Universiti Sains Malaysia'ya (USM)</span> gerçekleştirdiği resmi ziyaret kapsamında
                iki kurum arasındaki akademik iş birliğini güçlendirecek stratejik temaslarda bulundu.
              </p>

              <p className="text-lg leading-relaxed text-gray-700">
                <span className="font-semibold text-hacettepe-blue">Hacettepe Üniversitesi</span> ile
                <span className="font-semibold"> Universiti Sains Malaysia (USM)</span> arasında imzalanan
                Mutabakat Zaptı çerçevesinde düzenlenen ziyarette, Öğr. Gör. Dr. Davut Aksüt, iş birliği araştırmalarını
                geliştirmek ve akademik ortaklığı genişletmek amacıyla USM Mühendislik Kampüsü'nde çeşitli görüşmeler gerçekleştirmiştir.
              </p>
            </div>

            {/* Görüşmeler Bölümü */}
            <div className="bg-white p-6 rounded-2xl border-2 border-gray-100">
              <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="w-2 h-8 bg-hacettepe-blue rounded-full mr-3"></span>
                Stratejik Görüşmeler
              </h3>
              <p className="text-lg leading-relaxed text-gray-700 mb-4">
                Ziyaret kapsamında <span className="font-semibold">Mühendislik Kampüsü Direktörü Prof. Ir. Dr. Mariatti Jaafar @ Mustapha</span> ve
                <span className="font-semibold"> Malzeme ve Mineral Kaynaklar Mühendisliği Okulu Dekanı Prof. Ts. Dr. Zulkifli Mohamad Ariff</span> ile
                bir araya gelinmiştir.
              </p>
              <p className="text-lg leading-relaxed text-gray-700">
                Ayrıca, <span className="font-semibold">Doç. Ir. Nadras Binti Othman</span> ve
                <span className="font-semibold"> Prof. Dr. Hazizan Bin Md. Akil</span> ile yürütülecek bilimsel çalışmalara dair
                verimli toplantılar yapılmıştır.
              </p>
            </div>

            {/* Ortak Proje Bölümü */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-2xl border-2 border-purple-100">
              <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="w-2 h-8 bg-purple-600 rounded-full mr-3"></span>
                Ortak Proje ve Yayın Hazırlıkları
              </h3>
              <p className="text-lg leading-relaxed text-gray-700 mb-4">
                İmzalanan protokol çerçevesinde; özellikle <span className="font-semibold text-purple-700">elastomerler</span>,
                <span className="font-semibold text-purple-700"> ileri malzemeler</span> ve ilgili diğer temel alanlara odaklanan
                iş birliği araştırmalarını geliştirmek hedeflenmektedir.
              </p>
              <p className="text-lg leading-relaxed text-gray-700">
                Yapılan görüşmeler neticesinde, iki üniversite arasında karşılıklı projeler ve akademik çalışmalar yürütülmesine yönelik
                <span className="font-semibold"> ortak çalışma alanları belirlenmiş</span> olup,
                <span className="font-semibold text-hacettepe-blue"> proje ve yayın hazırlıklarına yönelik ön çalışmalara başlanmıştır</span>.
              </p>
            </div>
          </div>
        </div>

        {/* Fotoğraf Galerisi */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-extrabold text-gray-800 mb-4 tracking-tight">
              Ziyaretten Kareler
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-hacettepe-blue to-hacettepe-red mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {images.map((src, index) => (
              <div
                key={index}
                className="group relative aspect-video overflow-hidden rounded-2xl shadow-lg bg-gray-200 cursor-pointer transform hover:scale-105 transition-all duration-300"
                onClick={() => openModal(index)}
              >
                {/* Loading placeholder */}
                {!imageLoaded[index] && (
                  <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-hacettepe-blue border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}

                <img
                  src={src}
                  alt={`USM Ziyareti ${index + 1}`}
                  className={`w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-110 ${
                    imageLoaded[index] ? 'opacity-100' : 'opacity-0'
                  }`}
                  loading="eager"
                  onLoad={() => handleImageLoad(index)}
                  onError={() => handleImageLoad(index)}
                />

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500"></div>

                {/* Hover content */}
                <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-2 group-hover:translate-y-0">
                  <p className="text-sm font-medium">USM Ziyareti</p>
                  <p className="text-xs opacity-75">Fotoğraf {index + 1} • Tıklayın</p>
                </div>

                {/* Click indicator */}
                <div className="absolute top-4 right-4 bg-black bg-opacity-50 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <div className="w-4 h-4 border-2 border-white rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Geri Dön Butonu */}
        <div className="text-center">
          <Link
            to="/haberler"
            className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-gray-800 to-gray-700 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-gray-300"
          >
            <FaArrowLeft className="mr-3 -ml-1 h-5 w-5 group-hover:-translate-x-1 transition-transform duration-300" />
            Tüm Haberlere Geri Dön
          </Link>
        </div>

        {/* Image Modal */}
        <ImageModal />
      </div>
    </PageTemplate>
  );
};

export default DavutAksutUSMZiyareti;
